import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  FieldLabel,
  GroupField,
  Input,
  Dropdown,
  AutocompleteDropdown,
  TextArea,
  BooleanCheckbox,
  NumberInput,
} from "react-invenio-forms";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";
import { Label } from "semantic-ui-react";

const NestedField = ({
  fieldPath,
  icon,
  label,
  fields,
  description,
  multiple,
}) => {
  const [options, setOptions] = useState();

  const { i18n } = useTranslation();
  const formikProps = useFormikContext();

  const currentLanguage = i18n.language;

  const fetchVocabularyForField = async (field) => {
    try {
      const response = await axios.get(
        `/api/vocabularies/${field.vocabulary_id}?size=100`
      );

      const optionsData = response.data.hits.hits
        .sort((a, b) => +a.props?.order - +b.props?.order)
        .map((hit) => ({
          title_l10n: hit.title[currentLanguage] || hit.title.en,
          id: hit.id,
        }));

      return optionsData;
    } catch (error) {
      console.error("Vocabulary load error:", error);
      return null;
    }
  };

  useEffect(() => {
    const loadVocabularies = async () => {
      const vocabularies = {};

      for (const current of fields) {
        if (current.ui_widget === "Dropdown" && current.vocabulary_id) {
          const vocabulary = await fetchVocabularyForField(current);

          if (vocabulary) {
            vocabularies[current.name] = vocabulary;
          }
        }
      }

      setOptions(vocabularies);
    };

    loadVocabularies();
  }, [currentLanguage]);

  if (!options) {
    return null;
  }

  const renderInput = (field) => {
    const commonProps = {
      fieldPath: `${fieldPath}.${field.name}`,
      ...field.props,
    };
    switch (field.ui_widget) {
      case "Input":
        return <Input {...commonProps} />;
      case "Dropdown":
        return <Dropdown {...commonProps} options={options[field.name]} />;
      case "AutocompleteDropdown":
        return (
          <AutocompleteDropdown
            {...commonProps}
            autocompleteFrom={`/api/vocabularies/${field.vocabulary_id}`}
          />
        );
      case "TextArea":
        return <TextArea {...commonProps} />;
      case "Boolean":
        return <BooleanCheckbox {...commonProps} />;
      case "Number":
        return <NumberInput {...commonProps} />;
      default:
        return null;
    }
  };

  return (
    <div className="nested-field">
      {!multiple && (
        <FieldLabel htmlFor={fieldPath} icon={icon} label={label} />
      )}
      {description && <label className="helptext">{description}</label>}
      <GroupField
        className={multiple ? "multiple-nested-field" : "invenio-group-field"}
        fieldPath={fieldPath}
        optimized
      >
        {fields.map((field) => {
          const gridStyle = {
            gridColumn: `span ${field.size || 12}`,
          };

          var isError = false;
          var errorMessage = null;

          const fieldName = fieldPath.split(".")[1];
          const error = formikProps.errors.custom_fields?.[fieldName];

          if (error) {
            const [name, message] = error.split(":");

            if (field.name === name) {
              isError = true;
              errorMessage = message;
            }
          }

          return (
            <div
              className={`nested-field-item field${isError ? " error" : ""}`}
              style={gridStyle}
            >
              {renderInput(field)}
              {isError && errorMessage && (
                <Label className="prompt" pointing="above">
                  {errorMessage}
                </Label>
              )}
            </div>
          );
        })}
      </GroupField>
    </div>
  );
};

export default NestedField;
